import {call, put, race, select, take} from 'redux-saga/effects';
import {push as redirectTo} from 'redux-first-history';
import {HOME_URL, LOGIN_URL} from 'paths';

import {
    authenticateUser,
    authenticationError,
    cancelLogin,
    exitPasswordReset,
    getAuthMethodForUser,
    incrementFailedLoginAttempts,
    invalidateToken,
    logOut,
    logoutError,
    receiveAuthenticationMethod,
    receiveAuthenticationToken,
    resetFailedLoginAttempts,
    sessionTimeout,
    setLoginState,
    submitPassword,
    submitUsername
} from 'modules/authentication/actions';
import {fireAndForget} from 'modules/api/actions';
import {loginStates} from 'modules/authentication/constants';
import {loginStateSelector} from 'modules/authentication/selectors';
import {clearTokensAndData, recallLocation, setApiToken, setCurrentUsername} from 'modules/authentication/utils';
import {pathSelector} from 'modules/rootSelectors';
import {doApiCall} from 'modules/sagaUtils';
import {notifyUser} from 'modules/toast/actions';
import {clearUserData} from 'modules/user/actions';
import {authenticateWithOAuth2} from 'modules/authentication/sagas/oauth2';

export function* login() {
    while (true) {
        let loggedIn = false;

        const initialAction = yield race({
            username: take(submitUsername),
            passwordReset: take(exitPasswordReset),
            logOut: take(logOut),
            sessionTimeout: take(sessionTimeout),
        });

        try {
            if (!initialAction.username) {
                continue;
            }

            const username = initialAction.username.payload;

            let apiAction;
            try {
                apiAction = yield call(
                    doApiCall,
                    getAuthMethodForUser,
                    null,
                    {username},
                    receiveAuthenticationMethod,
                    authenticationError,
                );
            } catch (e) {
                yield put(notifyUser('There was an error making that request', 'error'));
                continue;
            }

            const type = apiAction.payload.data.type;

            try {
                switch (type) {
                    case 'oauth2':
                        yield call(
                            authenticateWithOAuth2,
                            username,
                            apiAction.payload.data.domain,
                            apiAction.payload.data.clientId,
                            apiAction.payload.data.identityProvider,
                        );
                        continue;

                    case 'auth0':
                    case 'basic':
                        yield call(authenticateWithPassword, username);
                        break;

                    default:
                        throw new Error('Unknown authentication method: "' + type + '"');

                }
            } catch (e) {
                if (e.message != 'Login cancelled') {
                    yield put(notifyUser('There was an error authenticating', 'error'));
                    yield put(incrementFailedLoginAttempts());
                }
                console.log('continuing.....', e);
                continue;
            }

            yield handleLoginSuccess(username);
            loggedIn = true;

            const end = yield race({
                logout: take(logOut),
                timeout: take(sessionTimeout)
            });

            if (end.logout) {
                yield call(
                    doApiCall,
                    invalidateToken,
                    null,
                    {username},
                    fireAndForget,
                    logoutError,
                );
            }

        } catch (e) {
            console.log(e);
            if (!loggedIn) {
                yield put(notifyUser('An error was encountered while authenticating', 'error'));
            }
        } finally {
            const loginState = yield select(loginStateSelector);
            if (loginState != 'external') {
                yield handleLogout();
            }
        }
    }

}

function* authenticateWithPassword(username) {

    yield put(setLoginState(loginStates.ENTER_PASSWORD));

    const passwordAction = yield race({
        password: take(submitPassword),
        cancelLogin: take(cancelLogin),
        exitPasswordReset: take(exitPasswordReset),
    });

    if (passwordAction.cancelLogin || passwordAction.exitPasswordReset) {
        throw new Error('Login cancelled');
    }

    const password = passwordAction.password.payload;

    const data = {
        username: username,
        password: password,
    };

    console.log('authenticating', username, 'using password');
    const apiAction = yield call(
        doApiCall,
        authenticateUser,
        data,
        null,
        receiveAuthenticationToken,
        authenticationError,
    );

    const token = apiAction.payload.data.token;
    const ttl = apiAction.payload.data.ttl;

    console.log('token expires in', ttl);
    setApiToken(token, ttl);

}

export function* handleLoginSuccess(username = null) {
    if (username) {
        setCurrentUsername(username);
    }
    yield put(setLoginState(loginStates.LOGGED_IN));
    yield put(resetFailedLoginAttempts());
    const currentPath = yield select(pathSelector);
    if (currentPath == LOGIN_URL) {
        const rememberedLocation = recallLocation();
        yield put(redirectTo(rememberedLocation || HOME_URL));
    }
}

export function* handleLogout() {
    clearTokensAndData();
    yield put(clearUserData({}));
    yield put(setLoginState(loginStates.ENTER_USERNAME));
    yield put(redirectTo(LOGIN_URL));
}